/**
 * getErrorMessage(err, fallback) — one readable string from anything a request can throw.
 *
 * FastAPI returns `detail` in three shapes, and rendering the wrong one crashes React
 * ("Objects are not valid as a React child"):
 *   400/403/404/409  { detail: 'Project has invoices' }
 *   422 (validation) { detail: [{ loc: ['body', 'name'], msg: 'Field required', type: 'missing' }, …] }
 *   custom           { detail: { msg | message: '…' } }
 * This always returns a string, so it is safe to render, toast or log.
 *
 *   catch (err) { notify.error('Could not save the stage', { description: getErrorMessage(err) }) }
 *   catch (err) { setError(getErrorMessage(err, 'Could not load stock')) }
 *
 * Order: response.data.detail → response.data.message / error → err.message → fallback.
 */

export const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

// FastAPI prefixes validation locations with where the value came from.
const LOCATION_ROOTS = new Set(['body', 'query', 'path', 'header', 'cookie']);

const cleanText = (value) => (typeof value === 'string' ? value.trim() : '');

/** 'name' / 'items.2.qty' from a pydantic `loc` array (without the 'body' root). */
function fieldName(loc) {
  if (!Array.isArray(loc)) return '';
  const parts = loc.filter((part) => typeof part === 'string' || typeof part === 'number');
  if (parts.length && LOCATION_ROOTS.has(parts[0])) parts.shift();
  return parts.join('.');
}

function itemMessage(item) {
  if (item === null || item === undefined) return '';
  if (typeof item === 'string') return item.trim();
  if (typeof item === 'number' || typeof item === 'boolean') return String(item);
  if (typeof item !== 'object') return '';
  const msg = cleanText(item.msg) || cleanText(item.message) || cleanText(item.detail);
  if (!msg) return '';
  const field = fieldName(item.loc);
  return field ? `${field}: ${msg}` : msg;
}

/** Text from a FastAPI `detail` (string, array of validation errors, or object); '' when none. */
export function formatErrorDetail(detail) {
  if (detail === null || detail === undefined) return '';
  if (Array.isArray(detail)) {
    const messages = detail.map(itemMessage).filter(Boolean);
    return [...new Set(messages)].join('; ');
  }
  return itemMessage(detail);
}

export function getErrorMessage(error, fallback = DEFAULT_ERROR_MESSAGE) {
  if (error === null || error === undefined) return fallback;
  if (typeof error === 'string') return error.trim() || fallback;
  const data = error?.response?.data;
  if (data !== null && data !== undefined) {
    if (typeof data === 'object') {
      const fromDetail = formatErrorDetail(data.detail);
      if (fromDetail) return fromDetail;
      const fromBody = itemMessage(data.message) || itemMessage(data.error);
      if (fromBody) return fromBody;
    } else if (typeof data === 'string') {
      const text = data.trim();
      // Ignore HTML error pages from proxies; keep short plain-text bodies.
      if (text && !text.startsWith('<') && text.length <= 300) return text;
    }
  }
  return cleanText(error?.message) || fallback;
}

export default getErrorMessage;
